import { useEffect, useMemo } from 'react'
import { useLingui } from '@lingui/react'
import { t } from '@lingui/macro'
import { useSnackbar } from 'notistack'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'

import { Stack } from '@mui/material'

import SwitchInputController from '@/components/ui/inputs/SwitchInputController'

import { useUpdateMyProfileMutation } from '@/core/services/auth'

import { useIsINSEnabled } from '@/shared/model/useIsINSEnabled'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { useAppSelector } from '@/redux/rootReducer'

import { getApiErrorMessage } from '@/utils/errors'
import { isStaff } from '@/utils/type-guards'

const schema = yup.object({
  insEnabled: yup.boolean(),
})

type PatientManagementFormData = yup.InferType<typeof schema>

export function PatientManagement() {
  useLingui()
  const { enqueueSnackbar } = useSnackbar()

  const authUser = useAppSelector(selectAuthUser)
  const { hasLicense: hasINSLicense } = useIsINSEnabled()

  const [updateMyProfile] = useUpdateMyProfileMutation()

  const insEnabled = useMemo(() => (isStaff(authUser) ? authUser.managePatientPreferences.insEnabled ?? false : false), [authUser])

  const rhForm = useForm<PatientManagementFormData>({ defaultValues: { insEnabled } })
  const { handleSubmit, reset, subscribe } = rhForm

  useEffect(() => {
    return subscribe({
      name: 'insEnabled',
      formState: { values: true },
      callback: () => {
        handleSubmit(async (data) => {
          try {
            await updateMyProfile({ managePatientPreferences: { insEnabled: data.insEnabled } }).unwrap()
            enqueueSnackbar(t`Your profile has been successfully modified.`, { variant: 'success' })
          } catch (error) {
            enqueueSnackbar(getApiErrorMessage(error, t`An error occurred while modifying your profile. Please try again!`), { variant: 'error' })
            reset()
          }
        })()
      },
    })
  }, [handleSubmit, subscribe])

  if (!hasINSLicense) return null

  return (
    <form style={{ minHeight: '100%' }}>
      <Stack height="100%" spacing={3}>
        <Stack alignItems="start">
          <SwitchInputController control={rhForm.control} label={t`Enable INS`} name="insEnabled" />
        </Stack>
      </Stack>
    </form>
  )
}
